import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { StageLayout } from '@/components/ui/StageLayout';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { ApiError } from '@/lib/api/client';
import { capitalizeNameParts, nameInputProps } from '@/lib/patientName';
import VoiceFormFiller from '../../components/VoiceFormFiller';
import MergePatientsModal from '../../components/MergePatientsModal';
import { searchPatients, type DuplicateMatch } from '../patients/api';
import type { Patient } from '../patients/types';
import { useDuplicateCheck } from './useDuplicateCheck';
import { useIntakeDraft } from './useIntakeDraft';
import type { VerifyFormData } from './types';

/**
 * Second screen migrated off Create React App — see HMS_V4_API_CONTRACT.md
 * and QueueView.tsx for the established pattern this follows: a drop-in
 * replacement for src/components/stages/VerifyStage.jsx with the identical
 * prop contract, swapped into the still-legacy VisitWorkflow.jsx container by
 * changing one import line. That container (703 lines: visit search,
 * rollback, read-only review, the whole 11-stage stepper) is untouched —
 * only this one child stage and RegisterStage.tsx alongside it are rebuilt.
 *
 * VoiceFormFiller and MergePatientsModal are both TypeScript now too (see
 * CLAUDE.md's Frontend TypeScript Migration State) — imported the same way
 * regardless, since neither is specific to this screen (MergePatientsModal
 * is also used from Patient Records).
 */

interface VerifyStageProps {
  completeStage: (stage: string, stageData?: Record<string, unknown>, notes?: string) => void | Promise<void>;
  loading: boolean;
  onMergeReturning: (patient: Patient | DuplicateMatch) => void | Promise<void>;
}

const CONFIDENCE_META: Record<DuplicateMatch['confidence'], { label: string; bg: string; border: string; text: string }> = {
  // These three, and `medium` specifically, are reproduced as exact legacy
  // hex values rather than mapped onto the shared danger/warn/info tokens in
  // theme.css. `high` and `medium`'s bg/border DO match --color-danger and
  // --color-warn exactly, but medium's ink (#92400E) is a distinct amber from
  // the shared --color-warn-ink (#B45309) — the same amber Queue's priority
  // badge uses for Urgent. Two independent occurrences is not yet three; see
  // QueueView.tsx's PRIORITY_STYLE for the same call made the same way.
  high: { label: 'Likely duplicate', bg: '#FEE2E2', border: '#DC2626', text: '#991B1B' },
  medium: { label: 'Possible match', bg: '#FEF3C7', border: '#D97706', text: '#92400E' },
  low: { label: 'Weak match', bg: '#F3F4F6', border: '#9CA3AF', text: '#4B5563' },
};

function ageFromDob(dob: string): string | null {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 0 ? String(age) : null;
}

const VOICE_FIELDS = [
  { key: 'name', label: 'Patient name', type: 'text' },
  { key: 'mobile', label: 'Mobile number', type: 'text' },
  { key: 'dob', label: 'Date of birth (YYYY-MM-DD)', type: 'text' },
  { key: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female', 'Other'] },
  { key: 'age', label: 'Age', type: 'number' },
];

export default function VerifyStage({ completeStage, loading, onMergeReturning }: VerifyStageProps) {
  const [form, setForm] = useState<VerifyFormData>({ name: '', mobile: '', dob: '', gender: '', age: '' });
  // null = not checked yet; [] = checked, none found; [...] = found
  const [matches, setMatches] = useState<DuplicateMatch[] | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualQ, setManualQ] = useState('');
  const [manualResults, setManualResults] = useState<Patient[]>([]);
  const [manualSearching, setManualSearching] = useState(false);
  const [mergeCandidate, setMergeCandidate] = useState<Patient | DuplicateMatch | null>(null);

  const duplicateCheck = useDuplicateCheck();

  // See useIntakeDraft's own header comment — autosaves whatever's been
  // typed here into intake_sessions so a browser closed mid-Verify isn't a
  // total loss.
  useIntakeDraft(form as unknown as Record<string, string>);

  // Pre-fill from Voice Intake's extracted fields, if this visit started
  // that way — see global.d.ts for why this reads off `window`.
  useEffect(() => {
    const v = window.__voiceIntakeData;
    if (v) {
      setForm((p) => ({
        ...p,
        name: v.name ? capitalizeNameParts(v.name) : p.name,
        mobile: v.mobile || p.mobile,
        dob: v.dob || p.dob,
        gender: v.gender || p.gender,
        age: v.age || p.age,
      }));
    }
  }, []);

  const set = (k: keyof VerifyFormData, v: string) => {
    setForm((p) => {
      // A name dictated by voice gets the same first-letter capitals as a typed one.
      const next = { ...p, [k]: k === 'name' ? capitalizeNameParts(v) : v };
      // DOB is the more reliable source of truth once known — auto-fill age
      // from it so staff do not have to compute/type both, while leaving age
      // itself still editable afterward.
      if (k === 'dob') {
        const computed = ageFromDob(v);
        if (computed !== null) next.age = computed;
      }
      return next;
    });
    setMatches(null);
  };

  const applyVoiceFields = (extracted: Record<string, string>) => {
    const cleaned = Object.fromEntries(Object.entries(extracted).filter(([, v]) => v !== '' && v != null));
    if (cleaned.dob && !cleaned.age) {
      const computed = ageFromDob(cleaned.dob);
      if (computed !== null) cleaned.age = computed;
    }
    setForm((p) => ({ ...p, ...cleaned }));
    setMatches(null);
  };

  const runCheck = () => {
    if (!form.name.trim() || !form.mobile.trim()) return;
    duplicateCheck.mutate(
      {
        name: form.name.trim(),
        mobile: form.mobile.trim(),
        dob: form.dob || null,
        gender: form.gender || null,
        age: form.age ? Number(form.age) : null,
      },
      { onSuccess: (data) => setMatches(data.matches || []) }
    );
  };

  const handleContinueAsNew = () => {
    completeStage(
      'verify',
      {
        is_new: true,
        duplicate_check_ran: matches !== null,
        match_count: (matches || []).length,
        name: form.name.trim(),
        mobile: form.mobile.trim(),
        dob: form.dob || null,
        gender: form.gender || null,
        age: form.age || null,
      },
      matches && matches.length
        ? 'Confirmed new patient — possible matches were reviewed and discarded'
        : 'Confirmed new patient — no matching records found'
    );
  };

  const manualSearch = async () => {
    if (!manualQ.trim()) return;
    setManualSearching(true);
    try {
      const data = await searchPatients(manualQ);
      setManualResults(Array.isArray(data) ? data : []);
    } catch {
      setManualResults([]);
    }
    setManualSearching(false);
  };

  const checkError =
    duplicateCheck.error instanceof ApiError
      ? duplicateCheck.error.message
      : !form.name.trim() || !form.mobile.trim()
        ? null // handled by the disabled state instead of an error banner
        : null;

  return (
    // `single` — this is genuinely a short form (identity fields, then a
    // duplicate-check result list), not a multi-column layout candidate; the
    // win here is one canonical width instead of this file's own
    // independently-chosen 640px (see docs/HMS_V4_UI_MODERNIZATION_ASSESSMENT.md's
    // P0 finding on the nine stages that each hard-capped their own width).
    // No StageActionBar: unlike a stage with one terminal commit, this
    // screen's next action is genuinely branched — check, then either
    // "continue as new", a per-match "merge & continue", or a manual-search
    // pick — and collapsing those into one generic bar would misstate what
    // is actually being decided.
    <StageLayout variant="single">
    <Card className="fade-in">
      <div className="mb-1.5 text-xl font-bold">🔍 Verify patient</div>
      <div className="mb-5 text-[13px] text-ink-muted">
        Enter the patient details below — we will automatically check for existing records before registration.
      </div>

      <VoiceFormFiller fields={VOICE_FIELDS} onFields={applyVoiceFields} label="🎙 Fill this form by voice" />

      {checkError && (
        <div className="mb-3.5 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{checkError}</div>
      )}

      <div className="grid2 mb-3.5">
        <FormField label="Patient name" required>
          <Input value={form.name} {...nameInputProps((value) => set('name', value))} />
        </FormField>
        <FormField label="Mobile number" required>
          <Input value={form.mobile} onChange={(e) => set('mobile', e.target.value)} />
        </FormField>
        <FormField label="Date of birth">
          <Input type="date" value={form.dob} onChange={(e) => set('dob', e.target.value)} />
        </FormField>
        <FormField label="Gender">
          <NativeSelect value={form.gender} onChange={(e) => set('gender', e.target.value)}>
            <option value="">Select</option>
            {(['Male', 'Female', 'Other'] as const).map((g) => (
              <option key={g}>{g}</option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField label="Age (years)">
          <Input type="number" value={form.age} onChange={(e) => set('age', e.target.value)} />
        </FormField>
      </div>

      <Button className="mb-[18px]" full onClick={runCheck} disabled={duplicateCheck.isPending} loading={duplicateCheck.isPending}>
        {duplicateCheck.isPending ? 'Checking existing records…' : '🔍 Check for existing records'}
      </Button>

      {matches !== null && (
        <div className="fade-in mb-[18px]">
          {matches.length === 0 ? (
            <div className="mb-3.5 rounded-[var(--radius)] border border-success-soft bg-success-soft px-3.5 py-3 text-[13px] text-success-ink">
              ✓ No matching records found — this looks like a new patient.
            </div>
          ) : (
            <>
              <div className="mb-2.5 text-xs font-semibold text-warn-ink">
                ⚠ {matches.length} possible existing record{matches.length === 1 ? '' : 's'} found — review before continuing
              </div>
              <div className="mb-3.5 flex flex-col gap-2.5">
                {matches.map((m) => {
                  const meta = CONFIDENCE_META[m.confidence] || CONFIDENCE_META.low;
                  return (
                    <div
                      key={m.id}
                      className="rounded-[var(--radius)] px-3.5 py-3"
                      style={{ border: `2px solid ${meta.border}`, background: meta.bg }}
                    >
                      <div className="mb-1.5 flex items-start justify-between gap-2.5">
                        <div>
                          <div className="text-sm font-bold">{m.name}</div>
                          <div className="mt-0.5 text-xs text-[#374151]">
                            {m.uhid} · {m.gender}, {m.age ?? '—'}y · {m.mobile}
                            {m.dob ? ` · DOB ${new Date(m.dob).toLocaleDateString('en-IN')}` : ''}
                          </div>
                        </div>
                        <span
                          className="whitespace-nowrap rounded-[10px] px-[9px] py-[3px] text-[10.5px] font-bold"
                          style={{ background: '#fff', color: meta.text, border: `1px solid ${meta.border}` }}
                        >
                          {meta.label} · {m.score}%
                        </span>
                      </div>
                      <div className="mb-2.5 text-xs" style={{ color: meta.text }}>
                        {m.reasons.join(' · ')}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="success" size="sm" disabled={loading} onClick={() => onMergeReturning(m)}>
                          ✅ This is the same patient — merge & continue
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="bg-white"
                          onClick={() => setMergeCandidate(m)}
                        >
                          🔗 Duplicate of a different record
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <Button full disabled={loading} onClick={handleContinueAsNew}>
            {matches.length ? 'None of these match — continue as new patient →' : 'Continue to registration →'}
          </Button>
        </div>
      )}

      <div className="border-t border-[#F3F4F6] pt-3">
        <Button variant="ghost" size="sm" onClick={() => setManualOpen((o) => !o)}>
          {manualOpen ? '▲ Hide manual search' : '▼ Or search manually by UHID / name / mobile'}
        </Button>
        {manualOpen && (
          <div className="mt-2.5">
            <div className="mb-2.5 flex gap-2">
              <Input
                placeholder="e.g. UH2501 or Ananya or 9876…"
                value={manualQ}
                onChange={(e) => setManualQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && manualSearch()}
              />
              <Button variant="ghost" className="shrink-0" onClick={manualSearch}>
                {manualSearching ? '…' : 'Search'}
              </Button>
            </div>
            {manualResults.map((p) => (
              <div
                key={p.id}
                className="mb-1.5 flex items-center justify-between gap-2 rounded-[var(--radius)] border border-line px-3 py-2.5"
              >
                <div>
                  <div className="text-[13px] font-semibold">{p.name}</div>
                  <div className="text-xs text-ink-muted">
                    {p.uhid} · {p.gender}, {p.age}y · {p.mobile}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <Button variant="ghost" size="sm" onClick={() => setMergeCandidate(p)}>
                    🔗 Merge with another record
                  </Button>
                  <Button variant="success" size="sm" disabled={loading} onClick={() => onMergeReturning(p)}>
                    ✅ Use this record
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {mergeCandidate && (
        <MergePatientsModal patientA={mergeCandidate} onClose={() => setMergeCandidate(null)} onSubmitted={() => setMergeCandidate(null)} />
      )}
    </Card>
    </StageLayout>
  );
}
